import cn from 'classnames'
import {
  IHRDatasetInfo,
  IHRSettings,
} from 'legacy/lut/classes/helpers/partner360/HistoricalResultsHelper'
import { usePartnerId } from 'legacy/lut/classes/helpers/partner360/usePartnerId'
import { usePartnerInfo } from 'legacy/lut/classes/helpers/partner360/usePartnerInfo'
import { IFormActualsTargetsSettings } from 'legacy/lut/classes/models/partner360/IFormActualsTargetsSettings'
import { ITranslationFactors } from 'legacy/lut/classes/models/partner360/ITranslationFactors'
import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'
import {
  changeContractualPlanning,
  selectFinancialYear,
  selectHasMultiLocalCurrencies,
  selectPartnerAccess,
  selectProgramType,
  selectProgramTypesExtended,
} from 'legacy/lut/features/partner360/configuration'
import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'
import { useSignature } from 'legacy/lut/hooks/useSignature'
import { IState } from 'legacy/lut/store'
import { Fragment, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'

import { InfoBlock, InfoBlockType } from 'cng-common/components/infoBlock'
import { Translation } from 'cng-common/features/translations/components/translation'
import { useSafetyTranslation } from 'cng-common/features/translations/hooks'
import { getUrlParam } from 'core-components/helpers/url'
import { Button } from 'core-components/library/button'
import { Switch } from 'core-components/library/checkbox'
import { ModalDialog } from 'core-components/library/modalDialog'
import type { IModalContent } from 'core-components/library/modalDialog/models/IModalContent'
import { NotificationController } from 'core-components/library/popup'
import MeasureController from 'core/analytics/MeasureController'
import { useMeasureController } from 'core/analytics/useMeasureController'
import { selectPublishingMode } from 'core/store/selectors/selectPublishingMode'
import Logger from 'core/utils/Logger'
import { useActualsTypes } from 'partner360/components/actualsTypeButtons'
import { useCurrencies } from 'partner360/components/currenciesButtons'
import { useNotApplicable } from 'partner360/components/finSob/components/notApplicableCategory/hooks/useNotApplicable'
import { NotApplicableCategory } from 'partner360/components/finSob/components/notApplicableCategory/NotApplicableCategory'
import { PLANNING_METRIC_URL_SEGMENT, PLANNING_SECTION_URL_SEGMENT } from 'partner360/configs'
import {
  type IAdditionalInfoItem,
  type IPlanningData,
  useLazyGetPlanningQuery,
} from 'partner360/entities/planning'
import { resetSobCompetitorsData } from 'partner360/features/competitorsSob'
import { FinancialPlanQuality } from 'partner360/features/financialPlanQuality'
import {
  changeFinSobApplicableSwitcherLoading,
  changeFinSobIsPartnerFlagsLoading,
  changeFinSobIsPlanningDataLoading,
  changeFinSobIsPlanningFactorsLoading,
  changeFinSobIsPlanningSettingsLoading,
} from 'partner360/features/finSob/finSobSlice'
import { selectFinSobSidePanelData } from 'partner360/features/finSobSidePanel'
import { requestCompletenessStatusForFinSobSidePanelItem } from 'partner360/features/finSobSidePanel/api/requestCompletenessStatusForFinSobSidePanelItem'
import type { IPlanningSmb } from 'partner360/features/planningSmb'
import { isSectionSettingOn } from 'partner360/helpers/sectionSettings/isSectionSettingOn'
import { useChangeNotApplicable } from 'partner360/hooks/finSob/useChangeNotApplicable'
import {
  focusJbpPlanActionOpener,
  isJbpPlanActionReturnFlow,
  postJbpPlanCompletedMessage,
  resolveJbpPlanActionParam,
} from 'partner360/lib/jbpPlanActionFlow'
import { partner360WidgetsBus } from 'partner360/lib/partner360WidgetsBus'
import { ActualsValueTypes } from 'partner360/models/actualValueTypes'
import { IFinSobSidePanelSetting } from 'partner360/models/finSobSidePanel/IFinSobSidePanelSetting'

import { requestRecalculateCompleteness } from '../../../../../features/partner360/completeness'
import {
  selectSectionSettings,
  updateSectionSettings,
} from '../../../../../features/partner360/sectionSettings'
import { createSectionSettingsPatch } from '../../../../../features/partner360/sectionSettings/models/iSectionSettingsState'
import { useConfirm } from '../../../../shared/Confirm/useConfirm'
import stylesCompensation from '../Compensation.module.scss'
import { IJBPPlanning } from '../models/IJBPPlanning'
import { IJBPMAP } from '../models/IJBPPMAP'
import stylesTable from '../PartnerDevicesInfo.module.scss'
import { applyMPSTargetsSplit } from '../services/api/applyMPSTargetsSplit'

import { CategoryPlanning } from './CategoryPlanning'
import { DataDisplaySettings } from './components/dataDisplaySettings/DataDisplaySettings'
import { useFocusOnKeyPress } from './components/floatingNumberInput'
import { GlobalCategorySwitcher } from './components/globalCategorySwitcher/GlobalCategorySwitcher'
import { PartnerFlags } from './components/partnerFlags/PartnerFlags'
import { SIGNATURE_DROP_DIALOG } from './components/powerServicesMembership'
import { RetailFinSob } from './components/retailFinSob/RetailFinSob'
import {
  GLOBAL_TOTAL_CATEGORY,
  HIDE_TNET_DISCLAIMER_FOR_COUNTRY,
  IS_ALWAYS_SHOW_ACTUALS,
  MANDATORY_CATEGORY_CONTRACTUAL_SUFFIX,
  MANDATORY_CATEGORY_VISIBLE_SUFFIX,
  OVERALL_CATEGORY,
  OVERALL_CATEGORY_MANDATORY_FLAG,
  P360_FIRST_FYEAR,
  PLAN_REQUEST_FOR_PREV_YEARS,
  PLANNING_TOTAL_CATEGORY,
  PlanningRounding,
  READONLY_FLAGS,
  SST_MANDATORY_LOGIC_FYEAR,
  TOP_PARTNER_FLAGS,
} from './config'
import { ConfirmMpsTargetsSplitDialog } from './dialogs/confirmMpsTargetsSplitDialog/ConfirmMpsTargetsSplitDialog'
import { ErrorLoadingDataDialogContent } from './dialogs/errorLoadingDataDialog/ErrorLoadingDataDialogContent'
import { MpsPlanNotCompletedDialog } from './dialogs/mpsPlanNotCompletedDialog/MpsPlanNotCompletedDialog'
import { getCategoryNAField } from './helpers/getCategoryNAField'
import { getNAField } from './helpers/getNAField'
import { getPlanningCurrency } from './helpers/getPlanningCurrency'
import { getRoundingField } from './helpers/getRoundingField'
import { isOfferingPlanningCompleted } from './helpers/isOfferingPlanningCompleted'
import { getPartnerFlagTag } from './helpers/partnerFlags/getPartnerFlagTag'
import { getTnetDisclaimerKey, isTnetDisclaimer } from './helpers/tnetDisclaimer'
import { useDisplaySettings } from './hooks/useDisplaySettings'
import { CategoryPlanningStandard } from './lookAndFeel/CategoryPlanningStandard'
import { TotalPlanningStandard } from './lookAndFeel/TotalPlanningStandard'
import { SETTINGS_CONTRACTUAL_SUFFIX, TSplitType } from './models'
import { IGlobalCategory } from './models/IGlobalCategory'
import { IPartnerFlag } from './models/IPartnerFlag'
import { TotalPlanning } from './TotalPlanning'

import styles from './Planning.module.scss'

interface IProps {
  widgetParams: IObjectAny
  metrics: MeasureController
  pid: string
  /** The offering to plan, when not the one picked: Overall's plan is `Consolidated`. */
  programType?: ProgramTypes
  readOnly?: boolean
  selectedFinSobItem?: IFinSobSidePanelSetting | null
  isEmbeded?: boolean
}

interface JbpPlanReturnModalContentProps extends IModalContent {
  onStay: () => void
  onGoBack: () => void
}

const JbpPlanReturnModalContent = ({ onStay, onGoBack }: JbpPlanReturnModalContentProps) => {
  return (
    <div className={styles.returnFlowModal}>
      <p className={styles.returnFlowModalMessage}>
        Your planning data was saved. Return to the previous tab?
      </p>
      <div className={styles.returnFlowModalActions}>
        <Button onClick={onStay} color="grey">
          Stay here
        </Button>
        <Button onClick={onGoBack}>Go back</Button>
      </div>
    </div>
  )
}

export const Planning = (props: IProps) => {
  const { selectedFinSobItem } = props
  const [getPlanning] = useLazyGetPlanningQuery()

  /** -------------------- State ----------------------- */

  const [hrDataSets, setHRDataSets] = useState<IHRDatasetInfo[]>([])
  const [hrDataSetsTotal, setHRDataSetsTotal] = useState<IHRDatasetInfo[]>([])
  const [hrFormats, setHRFormats] = useState<IFormActualsTargetsSettings[]>([])
  const [hrFormatsTotal, setHRFormatsTotal] = useState<IFormActualsTargetsSettings[]>([])
  const [isRegionalPlanning, setIsRegionalPlanning] = useState(true)
  const [isShowErrorLoadingDataDialog, setIsShowErrorLoadingDataDialog] = useState(false)
  const [isShowMpsTargetsSplitDialog, setIsShowMpsTargetsSplitDialog] = useState(false)
  const [isShowMpsPlanNotCompletedDialog, setIsShowMpsPlanNotCompletedDialog] = useState(false)
  const [globalCategories, setGlobalCategories] = useState<IGlobalCategory[]>([])
  const [globalCategory, setGlobalCategory] = useState<IGlobalCategory>()
  const [mapData, setMAPData] = useState<IJBPMAP[]>([])
  const [planningData, setPlanningData] = useState<IJBPPlanning[]>([])
  const [planningSmbData, setPlanningSmbData] = useState<IPlanningSmb[]>([])
  const [dirtyPlanningCategories, setDirtyPlanningCategories] = useState<ReadonlySet<string>>(
    () => new Set()
  )
  const [originalActualsValueType, setOriginalActualsValueType] = useState('')
  const [originalCurrency, setOriginalCurrency] = useState('')
  const [partnerFlagsState, setPartnerFlagsState] = useState<IPartnerFlag[]>([])
  const [translationFactors, setTranslationFactors] = useState<ITranslationFactors[] | null>(null)
  const [triggerApply, setTriggerApply] = useState(false)
  const [splitType, setSplitType] = useState<TSplitType>('Total')
  const [additionalInfo, setAdditionalInfo] = useState<IAdditionalInfoItem[]>()

  const [isShowValidationPopup, setIsShowValidationPopup] = useState(false)

  const [roundingValue, setRoundingValue] = useState<PlanningRounding>(PlanningRounding.AsIs)
  const [roundingValueOriginal, setRoundingValueOriginal] = useState<PlanningRounding>(
    PlanningRounding.AsIs
  )

  const [isShowComposedSoB, setIsShowComposedSoB] = useState(false)
  const [isShowJbpPlanReturnModal, setIsShowJbpPlanReturnModal] = useState(false)
  const [isQualityViewSelected, setIsQualityViewSelected] = useState(false)

  const divContainer = useRef<HTMLDivElement>(null)

  /**------------------------Selectors----------------------*/

  const isEditMode = useSelector(selectPublishingMode)
  const pickedProgramType = useSelector(selectProgramType)
  const programType = props.programType ?? pickedProgramType
  const fyear = useSelector(selectFinancialYear)
  const { isDistributor, isRegionalDistributor } = useSelector(selectPartnerAccess)
  const sectionSettings = useSelector(selectSectionSettings)
  const programTypesExtended = useSelector(selectProgramTypesExtended)
  const finSobSidePanelData = useSelector((state: IState) =>
    selectFinSobSidePanelData(state, programType)
  )

  /** -------------------- Hooks ----------------------- */

  const location = useLocation()

  // Display settings managed by custom hook
  const displaySettingsConfig = useMemo(
    () => ({
      programType,
      isConsolidated: programType === ProgramTypes.Consolidated,
      isRetailer: programType === ProgramTypes.Retail,
      isDistributor,
    }),
    [programType, isDistributor]
  )
  const displaySettings = useDisplaySettings(displaySettingsConfig)

  // Additional state not in displaySettings
  const [isShowActuals, setIsShowActuals] = useState(false)

  const handleSetIsRegionalPlanning = useCallback((value: boolean) => {
    setIsRegionalPlanning(value)
  }, [])

  const handleSetSplitType = useCallback((value: SetStateAction<TSplitType>) => {
    setSplitType(value)
  }, [])

  const handleSetRoundingValue = useCallback((value: PlanningRounding) => {
    setRoundingValue(value)
  }, [])

  const handleSetIsShowComposedSoB = useCallback((value: boolean) => {
    setIsShowComposedSoB(value)
  }, [])

  const handlePlanningDirtyStateChange = useCallback((category: string, isDirty: boolean) => {
    setDirtyPlanningCategories((currentCategories) => {
      if (currentCategories.has(category) === isDirty) {
        return currentCategories
      }

      const nextCategories = new Set(currentCategories)
      if (isDirty) {
        nextCategories.add(category)
      } else {
        nextCategories.delete(category)
      }

      return nextCategories
    })
  }, [])

  const onKeyPress = useFocusOnKeyPress()
  const { confirm } = useConfirm()
  const dispatch = useAppDispatch()
  const partnerId = usePartnerId(props.widgetParams, props.pid)
  const { startLoadData, stopLoadData } = useMeasureController(props.metrics)
  const { partnerInfo, canEditPartner, reloadPartnerInfo } = usePartnerInfo({
    partnerId,
  })
  const { onChangeNotApplicableFlag } = useNotApplicable({
    changeLoading: (isLoading) => dispatch(changeFinSobApplicableSwitcherLoading(isLoading)),
    partnerId,
    fYear: fyear,
    programType,
  })
  const { setCurrency } = useCurrencies()
  const hasMultiLocalCurrencies = useSelector(selectHasMultiLocalCurrencies)
  const { actualsType, onChangeActualsType } = useActualsTypes()
  const { signatureId, dropSignature } = useSignature()
  const onChangeNotApplicable = useChangeNotApplicable({ partnerId, fYear: fyear })
  const t = useSafetyTranslation()

  /** -------------------- Variables ----------------------- */

  const signed = useMemo(() => (signatureId ? 1 : -1), [signatureId])
  const actionParam = useMemo(
    () => resolveJbpPlanActionParam(getUrlParam(PLANNING_SECTION_URL_SEGMENT, location.pathname)),
    [location.pathname]
  )
  const urlActualsValueType = useMemo(() => {
    return getUrlParam(PLANNING_METRIC_URL_SEGMENT, location.pathname)
  }, [location.pathname])
  const isFromJbpPlanAction = useMemo(
    () => isJbpPlanActionReturnFlow(location.search),
    [location.search]
  )
  const isContractualsAvailable = useMemo(
    () => hrDataSets.some((el) => !!el.IsContractualsAvailable),
    [hrDataSets]
  )
  const isMPS = useMemo(() => !!partnerInfo?.IsMPS, [partnerInfo?.IsMPS])
  const isEditAllowed = useMemo(
    () =>
      !props.readOnly &&
      isEditMode &&
      canEditPartner &&
      (!isRegionalDistributor || isRegionalPlanning),
    [canEditPartner, isEditMode, isRegionalDistributor, isRegionalPlanning, props.readOnly]
  )
  const isShowContractualSwitcher = useMemo(
    () => isEditAllowed && isContractualsAvailable,
    [isContractualsAvailable, isEditAllowed]
  )
  const isShowPartnerFlagsSwitchers = useMemo(() => fyear >= SST_MANDATORY_LOGIC_FYEAR, [fyear])
  const isShowGlobalCategorySwitcher = useMemo(
    () => !!partnerInfo?.IsGlobal && programType !== ProgramTypes.Consolidated,
    [partnerInfo?.IsGlobal, programType]
  )
  const isShowGlobalCategoryDisclaimer = useMemo(() => {
    return !!partnerInfo?.IsGlobal && !!globalCategory && !globalCategory?.IsMainHierarchy
  }, [globalCategory, partnerInfo?.IsGlobal])
  const isShowTotal = useMemo(() => {
    const isCategorySelected =
      !selectedFinSobItem || selectedFinSobItem.Title === PLANNING_TOTAL_CATEGORY
    const isCategoryAvailable = !!hrDataSetsTotal.length
    const isConsolidatedView = programType === ProgramTypes.Consolidated

    return isCategorySelected && isCategoryAvailable && !isConsolidatedView
  }, [hrDataSetsTotal.length, programType, selectedFinSobItem])
  const memorizedDataSets = useMemo(
    () =>
      hrDataSets.map((settings: IHRDatasetInfo) => ({
        settings,
        formats: hrFormats.filter((el) => el.Category === settings.category),
      })),
    [hrDataSets, hrFormats]
  )
  const totalTnetInfoKey = useMemo(() => getTnetDisclaimerKey(hrDataSets, t), [hrDataSets, t])
  const globalCategoryName = useMemo(
    () => (!globalCategory?.GlobalCategory ? '' : t(globalCategory?.GlobalCategory)),
    [globalCategory, t]
  )
  const isRetailer = useMemo(() => programType === ProgramTypes.Retail, [programType])
  const isShowNewLnFTotal = useMemo(
    () =>
      isShowTotal &&
      displaySettings.settings.isNewLookAndFeel &&
      (!isRetailer || displaySettings.settings.isShowDetailedPlanning),

    [
      isShowTotal,
      displaySettings.settings.isNewLookAndFeel,
      isRetailer,
      displaySettings.settings.isShowDetailedPlanning,
    ]
  )
  const isPlanningSmbVisible = useMemo(
    () => isOfferingPlanningCompleted(finSobSidePanelData),
    [finSobSidePanelData]
  )
  const { isShowActualsTypeDisclaimer, actualsTypeDisclaimerKey } = useMemo(() => {
    const currentOffering = programTypesExtended.find((it) => it.Offering === programType)
    const actualsTypeDisclaimerKey = currentOffering?.PlanningDisclaimer?.TranslationKey
    const disclaimerValueTypes = currentOffering?.PlanningDisclaimer?.ActualsValueTypes

    const isTnetAvtDisclaimer =
      isTnetDisclaimer(actualsTypeDisclaimerKey, t) ||
      (actualsType === ActualsValueTypes.TNet &&
        disclaimerValueTypes?.includes(ActualsValueTypes.TNet))
    const isShowActualsTypeDisclaimer =
      !!actualsTypeDisclaimerKey &&
      !isTnetAvtDisclaimer &&
      (!disclaimerValueTypes?.length || disclaimerValueTypes.includes(actualsType))

    return { isShowActualsTypeDisclaimer, actualsTypeDisclaimerKey }
  }, [actualsType, programType, programTypesExtended, t])

  /** -------------------- Methods ----------------------- */

  const isContractualPlanning = useCallback(() => {
    let isContractual = isMPS ? isContractualsAvailable && isMPS : false

    if (partnerInfo) {
      const field = getNAField(programType, SETTINGS_CONTRACTUAL_SUFFIX)
      const val = sectionSettings[field]
      if (val !== undefined && val !== null) {
        isContractual = isSectionSettingOn(val)
      }
    }

    return isContractual
  }, [sectionSettings, programType, isContractualsAvailable, isMPS, partnerInfo])

  const fetchPartnerFlags = useCallback(async () => {
    dispatch(changeFinSobIsPartnerFlagsLoading(true))

    const partnerFlagsNames = (partnerInfo?.PartnerFlags || '').split(',')
    const partnerFlags: IPartnerFlag[] = []

    hrFormats.forEach((setting) => {
      const mandatoryByFlags = (setting.MandatoryByFlags || '').split(',')
      const unitsMandatoryByFlags = (setting.UnitsMandatoryByFlags || '').split(',')
      const sobMandatoryByFlags = (setting.SOBMandatoryByFlags || '').split(',')

      const settingFlags = [...mandatoryByFlags, ...unitsMandatoryByFlags, ...sobMandatoryByFlags]

      settingFlags
        .filter((el) => !!el && el !== OVERALL_CATEGORY_MANDATORY_FLAG)
        .forEach((settingFlag) => {
          let isVisible = true
          let isSkipTransactional = false

          if (!!settingFlag && settingFlag.endsWith(MANDATORY_CATEGORY_VISIBLE_SUFFIX)) {
            settingFlag = settingFlag.substring(0, settingFlag.length - 1)
            isVisible = false
          }
          if (!!settingFlag && settingFlag.endsWith(MANDATORY_CATEGORY_CONTRACTUAL_SUFFIX)) {
            settingFlag = settingFlag.substring(0, settingFlag.length - 1)
            isSkipTransactional = true
          }

          const foundFlags = partnerFlags.filter((el) => el.name === settingFlag)
          if (!foundFlags.length) {
            const isPartnerFlag = partnerFlagsNames.includes(settingFlag)
            const isReadonly = READONLY_FLAGS.indexOf(settingFlag) >= 0
            const isTopPartner = TOP_PARTNER_FLAGS.indexOf(settingFlag) >= 0

            const tag = getPartnerFlagTag(settingFlag)
            const flagFromSettings = sectionSettings[tag]

            // Get Flag state from settings (if settings exist)
            let isSettingPresent = false
            let isFlagManuallyEnabled = false
            if (flagFromSettings !== undefined && flagFromSettings !== null) {
              isSettingPresent = true
              isFlagManuallyEnabled = isSectionSettingOn(flagFromSettings)
            }

            // Flags enabled by default for TopPartners and PartnerFlags (if no settings)
            const isForceEnabled = isPartnerFlag && (isTopPartner || !isSettingPresent)
            const isFlagEnabled = isForceEnabled ? true : isFlagManuallyEnabled

            // Force visibility for Partner level flag
            if (isPartnerFlag) {
              isVisible = settingFlag !== 'IsTopLF'
            }

            const disablingUser = sectionSettings[`${tag}-user`]
            const disablingDate = sectionSettings[`${tag}-date`]
            const disablingComment = sectionSettings[`${tag}-comment`]

            partnerFlags.push({
              name: settingFlag,
              tag,
              isPartnerFlag,
              isReadonly,
              isTopPartner,
              isEnabled: isFlagEnabled,
              isVisible,
              isSkipTransactional,

              disablingUser,
              disablingDate,
              disablingComment,
            })
          }
        })
    })

    setPartnerFlagsState(partnerFlags)

    dispatch(changeFinSobIsPartnerFlagsLoading(false))
  }, [dispatch, sectionSettings, hrFormats, partnerInfo?.PartnerFlags])

  const setOriginalSwitcherValues = useCallback(
    (allPlanningData: IJBPPlanning[]) => {
      // Multiple local currencies require USD; otherwise keep the first Target row's currency.
      const planningCurrency = getPlanningCurrency(allPlanningData, hasMultiLocalCurrencies)
      if (planningCurrency) {
        setCurrency(planningCurrency)
        setOriginalCurrency(planningCurrency)
      }
      const targetData = allPlanningData.filter((el) => !!el.IsTarget)
      if (targetData.length) {
        if (targetData[0].NetNdp) {
          onChangeActualsType(targetData[0].NetNdp as ActualsValueTypes)
          setOriginalActualsValueType(targetData[0].NetNdp)
        }
      }
    },
    [hasMultiLocalCurrencies, setCurrency, onChangeActualsType]
  )

  /**
   * Prepare settings for Planning sections
   */
  const parsePlanningSettings = useCallback(
    (settingsData: IFormActualsTargetsSettings[]) => {
      if (settingsData.length) {
        dispatch(changeFinSobIsPlanningSettingsLoading(true))

        setHRFormats(settingsData)

        let category = ''
        const categoriesSettings: IHRDatasetInfo[] = []
        let parsedGlobalCategories: IGlobalCategory[] = []
        settingsData.forEach((el) => {
          let settings: IHRSettings = {}
          try {
            if (el.Settings) {
              settings = JSON.parse(el.Settings)
            }
          } catch {
            Logger.error('Fail to parse el.Settings')
          }

          if (
            !!partnerInfo?.IsGlobal &&
            el.GlobalCategorySettings &&
            (!parsedGlobalCategories || !parsedGlobalCategories.length)
          ) {
            try {
              parsedGlobalCategories = JSON.parse(el.GlobalCategorySettings)
              parsedGlobalCategories = parsedGlobalCategories.sort(
                (a, b) => a.GlobalCategoryOrder - b.GlobalCategoryOrder
              )
              const isTotalPresent = !!parsedGlobalCategories.filter(
                (el) => el.GlobalCategory === GLOBAL_TOTAL_CATEGORY
              ).length
              if (isTotalPresent && parsedGlobalCategories.length === 2) {
                parsedGlobalCategories = parsedGlobalCategories.filter(
                  (el) => el.GlobalCategory !== GLOBAL_TOTAL_CATEGORY
                )
              }
            } catch {
              Logger.error('Fail to parse el.GlobalCategorySettings')
            }
          }

          if (
            category !== el.Category &&
            !categoriesSettings.find((hi) => hi.category === el.Category)
          ) {
            category = el.Category

            // Temporally hide MPS Summary Chart
            if (!el.SummaryProgram) {
              categoriesSettings.push({
                category: category,
                categoryTranslation: t(el.Category),
                subCategory: el.SubCategory,
                colors: (el.Colors || '').split(';'),
                ProgramType: el.ProgramType,
                SummaryProgram: !!el.SummaryProgram,
                Settings: settings,
                IsContractualsAvailable: el.IsContractualsAvailable,
                IsSoBAvailable: el.IsSoBAvailable,
                IsSoBTotalCalc: el.IsSoBTotalCalc,
                IsUnitsAvailable: el.IsUnitsAvailable,
                IsHideFinancial: el.IsHideFinancial,
                IsOnlyQuarterlyPlanning: el.IsOnlyQuarterlyPlanning,
                IsMainHierarchy: el.IsMainHierarchy,
                MandatoryType: el.MandatoryType,
                MandatoryByFlags: el.MandatoryByFlags,
                UnitsMandatoryByFlags: el.UnitsMandatoryByFlags,
              })
            }
          }
        })
        setHRDataSets(categoriesSettings)
        setGlobalCategories(parsedGlobalCategories)
        if (parsedGlobalCategories?.length) {
          setGlobalCategory(parsedGlobalCategories[0])
        }

        // Prepare settings for Total section

        const totalOffering = (programType || '').replaceAll(' ', '').toLocaleLowerCase()
        const totalCategory = `jbp-partner-type-${totalOffering}`

        const settingsDataForTotal = settingsData
          .filter((el, idx) => idx < 1 && (!el.SubCategory || el.SubCategory === OVERALL_CATEGORY))
          .map((el) => ({ ...el, Category: totalCategory }))
        setHRFormatsTotal(settingsDataForTotal)

        const categoriesSettingsForTotal = categoriesSettings.filter(
          (el) => !el.subCategory || el.subCategory === OVERALL_CATEGORY
        )
        setHRDataSetsTotal(categoriesSettingsForTotal)

        dispatch(changeFinSobIsPlanningSettingsLoading(false))
      }
    },
    [dispatch, partnerInfo?.IsGlobal, programType, t]
  )

  const fetchFinSobData = useCallback(async (): Promise<boolean> => {
    dispatch(changeFinSobIsPlanningDataLoading(true))
    dispatch(changeFinSobIsPlanningFactorsLoading(true))
    startLoadData()

    let finSobData: IPlanningData | undefined
    let isErrorLoadingData = false
    try {
      const fromFYear = Math.max(fyear - PLAN_REQUEST_FOR_PREV_YEARS, P360_FIRST_FYEAR)

      finSobData = await getPlanning({
        partnerId,
        fYear: fyear,
        fromFYear,
        programType,
        isRegionalDistributor,
      }).unwrap()

      if (!finSobData.Planning.length) {
        NotificationController.showWarning({
          message: 'Financial and SOB data are not available.',
        })
      }
    } catch (error) {
      isErrorLoadingData = true
    }

    if (finSobData) {
      // Set Planning Data
      const allPlanningData = finSobData.Planning || []
      setPlanningData(allPlanningData)
      setOriginalSwitcherValues(allPlanningData)

      // Set SMB vs EU split data
      setPlanningSmbData(finSobData.PlanningSMB || [])

      // Set MAP data
      setMAPData(finSobData.Map || [])

      // Set Planning Settings
      parsePlanningSettings(finSobData.Settings || [])

      // Set Translation Factors
      setTranslationFactors(finSobData.TranslationFactors || [])

      // Additional Planning Info
      setAdditionalInfo(finSobData.AdditionalInfo)
    }

    stopLoadData()
    dispatch(changeFinSobIsPlanningFactorsLoading(false))
    dispatch(changeFinSobIsPlanningDataLoading(false))
    if (isErrorLoadingData) {
      setIsShowErrorLoadingDataDialog(true)
    }

    return !isErrorLoadingData
  }, [
    dispatch,
    startLoadData,
    setOriginalSwitcherValues,
    parsePlanningSettings,
    stopLoadData,
    fyear,
    partnerId,
    programType,
    isRegionalDistributor,
    getPlanning,
  ])

  const reloadPlanningDataAfterSmbSave = useCallback(async () => {
    const isReloaded = await fetchFinSobData()
    if (!isReloaded) {
      throw new Error('Failed to reload planning data after saving the SMB vs EU split')
    }
  }, [fetchFinSobData])

  const updateMPSTargetsSplit = useCallback(async () => {
    dispatch(changeFinSobIsPlanningDataLoading(true))
    startLoadData()

    const fromFYear = Math.max(fyear - PLAN_REQUEST_FOR_PREV_YEARS, P360_FIRST_FYEAR)
    const planningResult = await applyMPSTargetsSplit<IJBPPlanning>({
      partnerId,
      fyear,
      fromFYear,
      programType,
      isRegionalDistributor,
    })
    const allPlanningData = planningResult?.Data || []
    setPlanningData(allPlanningData)
    setOriginalSwitcherValues(allPlanningData)

    await reloadPartnerInfo()

    await dispatch(
      requestRecalculateCompleteness({
        partnerId,
        fYear: fyear,
      })
    ).unwrap()

    stopLoadData()
    dispatch(changeFinSobIsPlanningDataLoading(false))

    setIsShowMpsPlanNotCompletedDialog(true)
  }, [
    dispatch,
    partnerId,
    fyear,
    programType,
    isRegionalDistributor,
    reloadPartnerInfo,
    setOriginalSwitcherValues,
    startLoadData,
    stopLoadData,
  ])

  /** -------------------- User actions handlers ----------------------- */

  const setIsContractualPlanning = useCallback(
    async (newState: boolean) => {
      if (signed === 1) {
        const isConfirmed = await confirm(SIGNATURE_DROP_DIALOG)
        if (!isConfirmed) {
          return
        }
      }

      dispatch(changeFinSobIsPlanningSettingsLoading(true))

      const newValue = newState ? 1 : 0
      const field = getNAField(programType, SETTINGS_CONTRACTUAL_SUFFIX)
      const settingForUpdate = createSectionSettingsPatch({ [field]: newValue })

      dispatch(changeContractualPlanning(newState))
      await dispatch(
        updateSectionSettings({
          sectionSettings: settingForUpdate,
          flag: field,
          state: newValue,
          controlName: 'Planning',
        })
      ).unwrap()
      await dropSignature('Financial & SOB Targets')

      dispatch(changeFinSobIsPlanningSettingsLoading(false))
    },
    [confirm, dispatch, programType, signed, dropSignature]
  )

  const updatePartnerFlag = async (tag: string, isEnabled: boolean, comment: string) => {
    dispatch(changeFinSobIsPlanningSettingsLoading(true))

    // Update PartnerFlag in Settings.
    // Disabling keeps the comment; the server adds `${tag}-user` and `${tag}-date`.
    // Enabling sends 0, so the server deletes all three.
    const state = isEnabled ? 1 : 0

    await dispatch(
      updateSectionSettings({
        flag: tag,
        state,
        comment: isEnabled ? undefined : comment,
        saveExtendedInfo: isEnabled ? 0 : 1,
        controlName: 'Planning',
      })
    ).unwrap()

    // Update local PartnerFlags
    const newAllPartnerFlags = partnerFlagsState.map((el) => {
      const isCurrentItem = el.tag === tag

      return { ...el, isEnabled: isCurrentItem ? isEnabled : el.isEnabled }
    })
    setPartnerFlagsState(newAllPartnerFlags)

    // Turn on mandatory Categories by Partner Flag
    if (isEnabled) {
      const categoriesForEnable: string[] = []
      memorizedDataSets
        .filter(({ settings }) => !settings.SummaryProgram)
        .forEach(({ settings, formats }) => {
          const category = settings.category

          // Collect all flags of SubCategories
          let categoryFlags: string[] = []
          formats
            .filter((el) => el.Category === category)
            .forEach((format) => {
              const mandatoryByFlags = (format.MandatoryByFlags || '').split(',')
              const unitsMandatoryByFlags = (format.UnitsMandatoryByFlags || '').split(',')
              const sobMandatoryByFlags = (format.SOBMandatoryByFlags || '').split(',')

              categoryFlags = [
                ...categoryFlags,

                ...mandatoryByFlags,
                ...unitsMandatoryByFlags,
                ...sobMandatoryByFlags,
              ]
            })

          // Compare Category flags with Partner flag
          const mandatoryCategories = categoryFlags.filter((el) => {
            let flag = el.replaceAll(MANDATORY_CATEGORY_VISIBLE_SUFFIX, '')
            flag = flag.replaceAll(MANDATORY_CATEGORY_CONTRACTUAL_SUFFIX, '')

            return !!flag && tag.indexOf(flag.toLowerCase()) >= 0
          })

          // Prepare list of Disbled categories
          if (mandatoryCategories.length) {
            const categoryNAField = getCategoryNAField(category)
            const settingVal = sectionSettings[`${categoryNAField}`]
            if (isSectionSettingOn(settingVal)) {
              categoriesForEnable.push(category)
            }
          }
        })

      // Enable Categories
      for (let i = 0; i < categoriesForEnable.length; i++) {
        await onChangeNotApplicable({
          isNotApplicable: 0,
          categoryField: categoriesForEnable[i],
          isNeedDropCommitment: false,
        })
      }
    }

    //recalculate completeness
    await dispatch(requestRecalculateCompleteness({ partnerId, fYear: fyear })).unwrap()

    //update side panel
    if (selectedFinSobItem?.Title) {
      await dispatch(
        requestCompletenessStatusForFinSobSidePanelItem({
          partnerId,
          programType,
          fYear: fyear,
          category: selectedFinSobItem.Title,
        })
      ).unwrap()
    }

    dispatch(changeFinSobIsPlanningSettingsLoading(false))
  }

  const onApplyChanges = (data: IJBPPlanning[]) => {
    const allPlanningData = data || []
    setPlanningData(allPlanningData)
    setOriginalSwitcherValues(allPlanningData)
    dispatch(resetSobCompetitorsData(programType))
  }

  const onReloadPartnerInfo = async () => {
    await reloadPartnerInfo()
    setIsShowMpsPlanNotCompletedDialog(true)
  }

  const handlePlanningSaveSuccess = useCallback(() => {
    if (!isFromJbpPlanAction) {
      return
    }

    setIsShowJbpPlanReturnModal(true)
  }, [isFromJbpPlanAction])

  const handleStayInPlanning = useCallback(() => {
    setIsShowJbpPlanReturnModal(false)
  }, [])

  const handleReturnToPreviousTab = useCallback(() => {
    postJbpPlanCompletedMessage({
      partnerId,
      fyear,
      offering: programType,
      actionParam,
    })

    focusJbpPlanActionOpener()
    window.close()
    setIsShowJbpPlanReturnModal(false)
  }, [actionParam, fyear, partnerId, programType])

  /** -------------------- Effects ----------------------- */

  useEffect(() => {
    let planningRounding = PlanningRounding.AsIs

    const field = getRoundingField(programType)
    const settingVal = sectionSettings[field]
    switch (settingVal) {
      case PlanningRounding.K:
        planningRounding = PlanningRounding.K
        break
      case PlanningRounding.M:
        planningRounding = PlanningRounding.M
        break
      default:
        break
    }

    setRoundingValue(planningRounding)
    setRoundingValueOriginal(planningRounding)
  }, [programType, sectionSettings])

  useEffect(() => {
    setHRDataSets([])
    setHRFormats([])

    if (partnerId && fyear && programType && partnerInfo) {
      fetchFinSobData()
    }
  }, [partnerId, fyear, programType, fetchFinSobData, isRegionalDistributor, partnerInfo])

  useEffect(() => {
    const unsubscribe = partner360WidgetsBus.subscribe('planningRefresh', () => {
      fetchFinSobData()
    })

    return unsubscribe
  }, [fetchFinSobData])

  useEffect(() => {
    if (triggerApply) {
      setTriggerApply(false)
    }
  }, [triggerApply])

  useEffect(() => {
    if (hrFormats.length) {
      fetchPartnerFlags()
    }
  }, [hrFormats, fetchPartnerFlags])

  useEffect(() => {
    if (isEditAllowed && !!partnerInfo?.MPSSplit) {
      setIsShowMpsTargetsSplitDialog(true)
    }
  }, [isEditAllowed, partnerInfo?.MPSSplit])

  useEffect(() => {
    const isConsolidated = programType === ProgramTypes.Consolidated
    const isRetailer = programType === ProgramTypes.Retail

    displaySettings.actions.updateSettings({
      isShowPlanAchievement: !isConsolidated && !isRetailer,
      isShowYoYActualsVsActuals: !isConsolidated && !isRetailer,
      isShowYoYActualsVsPlans: !isConsolidated,
      isShowPartnerCount: !isConsolidated && !isRetailer,
    })
  }, [programType, displaySettings.actions])

  useEffect(() => {
    const onEnterPress = (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        onKeyPress()
      }
    }

    document.addEventListener('keypress', onEnterPress)
    return () => {
      document.removeEventListener('keypress', onEnterPress)
    }
  }, [onKeyPress])

  /** -------------------- Render ----------------------- */

  if (selectedFinSobItem && !selectedFinSobItem.IsApplicable) {
    return (
      <NotApplicableCategory
        isEditMode={isEditMode}
        selectedFinSobItem={selectedFinSobItem}
        fYear={fyear}
        partnerId={partnerId}
        programType={programType}
        onChangeNotApplicableFlag={onChangeNotApplicableFlag}
      />
    )
  }

  return (
    <div ref={divContainer} className={styles.root}>
      {isShowGlobalCategorySwitcher && (
        <GlobalCategorySwitcher
          globalCategories={globalCategories}
          globalCategory={globalCategory}
          setGlobalCategory={setGlobalCategory}
          isQualityViewSelected={isQualityViewSelected}
          onQualityViewChange={setIsQualityViewSelected}
        />
      )}
      {isQualityViewSelected && (
        <FinancialPlanQuality
          partnerId={partnerId}
          fYear={fyear}
          programType={programType}
          isGlobal={!!partnerInfo?.IsGlobal}
          onCategoryPlanningClick={() => setIsQualityViewSelected(false)}
        />
      )}
      {!isQualityViewSelected && (
        <Fragment>
          {isRegionalDistributor && (
            <div className={styles.root}>
              <table className={cn(stylesTable.table, stylesCompensation.table, styles.table)}>
                <caption>
                  <div className={cn(styles.infoContainer)}>
                    <i
                      className={cn('kbase kbase-warning-alt', styles.infoIcon)}
                      aria-hidden="true"
                    />
                    <Translation
                      text={
                        isRegionalPlanning
                          ? 'jbp-distributor-regional-planning-info'
                          : 'jbp-distributor-group-planning-info'
                      }
                      actionsInside
                    />
                  </div>
                </caption>
              </table>
            </div>
          )}
          {isShowPartnerFlagsSwitchers && (
            <PartnerFlags
              isEditMode={isEditMode}
              partnerFlags={partnerFlagsState}
              onUpdateFlag={updatePartnerFlag}
            />
          )}
          {isShowContractualSwitcher && (
            <div className={styles.switchersContainer}>
              <div className={styles.switcher}>
                <Switch
                  padding
                  checked={isContractualPlanning()}
                  onChange={setIsContractualPlanning}
                />
                <div className={styles.switcherText}>
                  <Translation span text="jbp-planning-contractual-switcher" />
                </div>
              </div>
            </div>
          )}
          <DataDisplaySettings
            isDistributor={isDistributor}
            isRetailer={isRetailer}
            settings={displaySettings.settings}
            actions={displaySettings.actions}
          />
          {isShowGlobalCategoryDisclaimer && (
            <div className={styles.hierarchyDisclaimer}>
              <InfoBlock
                text={`jbp-planning-hierarchy-disclaimer`}
                replaceParams={[{ key: '{category}', value: globalCategoryName }]}
                type={InfoBlockType.Warning}
              />
            </div>
          )}
          {isShowActualsTypeDisclaimer && (
            <div className={styles.hierarchyDisclaimer}>
              <InfoBlock
                text={actualsTypeDisclaimerKey}
                replaceParams={[{ key: '{actualsType}', value: actualsType }]}
                type={InfoBlockType.Warning}
              />
            </div>
          )}
          {isShowTotal &&
            totalTnetInfoKey &&
            partnerInfo?.CountryCode !== HIDE_TNET_DISCLAIMER_FOR_COUNTRY && (
              <div className={styles.hierarchyDisclaimer}>
                <InfoBlock text={totalTnetInfoKey} type={InfoBlockType.Warning} />
              </div>
            )}
          {isShowNewLnFTotal && (
            <TotalPlanning
              key={PLANNING_TOTAL_CATEGORY}
              {...props}
              planningData={planningData}
              planningSmbData={planningSmbData}
              planningSmbFormats={hrFormats}
              isOfferingPlanningCompleted={isPlanningSmbVisible}
              isPlanningSmbEditAllowed={!dirtyPlanningCategories.size}
              onReloadPlanningData={reloadPlanningDataAfterSmbSave}
              allDataSets={hrDataSetsTotal}
              dataSetInfo={hrDataSetsTotal[0]}
              formats={hrFormatsTotal}
              isShowFutureYearsPlans={true}
              isRegionalPlanning={isRegionalPlanning}
              setIsRegionalPlanning={(isRegionalPlanning) =>
                setIsRegionalPlanning(isRegionalPlanning)
              }
              isContractualPlanning={isContractualPlanning()}
              isContractualsAvailable={isContractualsAvailable}
              isShowOnlyCompletedPlans={false}
              partnerFlagsState={partnerFlagsState}
              splitType={splitType}
              setSplitType={handleSetSplitType}
              globalCategory={globalCategory}
              displaySettings={displaySettings}
              showActuals={isShowActuals || IS_ALWAYS_SHOW_ACTUALS}
              setShowActuals={setIsShowActuals}
              isDistributorPlanner={isDistributor}
              roundingValue={roundingValue}
              setRoundingValue={handleSetRoundingValue}
              roundingValueOriginal={roundingValueOriginal}
              urlActualsValueType={urlActualsValueType}
            />
          )}
          {isShowTotal && !displaySettings.settings.isNewLookAndFeel && (
            <TotalPlanningStandard
              key={PLANNING_TOTAL_CATEGORY}
              {...props}
              planningData={planningData}
              allDataSets={hrDataSetsTotal}
              dataSetInfo={hrDataSetsTotal[0]}
              formats={hrFormatsTotal}
              showActuals={isShowActuals || IS_ALWAYS_SHOW_ACTUALS}
              setShowActuals={(show) => setIsShowActuals(show)}
              showQuota={displaySettings.settings.isShowQuotaTargets}
              setShowQuota={displaySettings.actions.setIsShowQuotaTargets}
              isShowFutureYearsPlans={true}
              isRegionalPlanning={isRegionalPlanning}
              setIsRegionalPlanning={(isRegionalPlanning) =>
                setIsRegionalPlanning(isRegionalPlanning)
              }
              isContractualPlanning={isContractualPlanning()}
              isContractualsAvailable={isContractualsAvailable}
              isShowOnlyCompletedPlans={false}
              partnerFlagsState={partnerFlagsState}
              splitType={splitType}
              setSplitType={setSplitType}
              globalCategory={globalCategory}
              urlActualsValueType={urlActualsValueType}
            />
          )}
          {memorizedDataSets
            .filter(({ settings }) => !settings.SummaryProgram)
            .map(({ settings, formats }, idx) => {
              const isShowCategory =
                !selectedFinSobItem || isShowTotal || selectedFinSobItem.Title === settings.category

              if (!isShowCategory) {
                return <Fragment key={`jbp-planning-${settings.category}-${idx}-hidden`} />
              } else if (displaySettings.settings.isNewLookAndFeel) {
                return isRetailer && !displaySettings.settings.isShowDetailedPlanning ? (
                  <RetailFinSob
                    key={`jbp-planning-${settings.category}-${idx}`}
                    {...props}
                    planningData={planningData}
                    mapData={mapData}
                    dataSetInfo={settings}
                    allDataSets={hrDataSets}
                    formats={formats}
                    isShowFutureYearsPlans={true}
                    isRegionalPlanning={isRegionalPlanning}
                    setIsRegionalPlanning={handleSetIsRegionalPlanning}
                    isContractualPlanning={isContractualPlanning()}
                    isShowOnlyCompletedPlans={false}
                    onApply={onApplyChanges}
                    onReloadPartnerInfo={onReloadPartnerInfo}
                    originalCurrency={originalCurrency}
                    originalActualsValueType={originalActualsValueType}
                    translationFactors={translationFactors}
                    partnerFlagsState={partnerFlagsState}
                    splitType={splitType}
                    setSplitType={handleSetSplitType}
                    globalCategory={globalCategory}
                    displaySettings={displaySettings}
                    showActuals={isShowActuals || IS_ALWAYS_SHOW_ACTUALS}
                    setShowActuals={setIsShowActuals}
                    showQuota={displaySettings.settings.isShowQuotaTargets}
                    setShowQuota={displaySettings.actions.setIsShowQuotaTargets}
                    isDistributorPlanner={isDistributor}
                    roundingValue={roundingValue}
                    setRoundingValue={handleSetRoundingValue}
                    roundingValueOriginal={roundingValueOriginal}
                    isShowValidationPopup={isShowValidationPopup}
                    setIsShowValidationPopup={setIsShowValidationPopup}
                    isShowTotal={isShowTotal}
                    additionalInfo={additionalInfo}
                    onSaveSuccess={handlePlanningSaveSuccess}
                    urlActualsValueType={urlActualsValueType}
                  />
                ) : (
                  <CategoryPlanning
                    key={`jbp-planning-${settings.category}-${idx}`}
                    {...props}
                    planningData={planningData}
                    planningSmbData={planningSmbData}
                    isOfferingPlanningCompleted={isPlanningSmbVisible}
                    onReloadPlanningData={reloadPlanningDataAfterSmbSave}
                    onPlanningDirtyStateChange={handlePlanningDirtyStateChange}
                    isPlanningSmbEditAllowed={!dirtyPlanningCategories.size}
                    mapData={mapData}
                    dataSetInfo={settings}
                    allDataSets={hrDataSets}
                    formats={formats}
                    planningSmbFormats={hrFormats}
                    isShowFutureYearsPlans={true}
                    isRegionalPlanning={isRegionalPlanning}
                    setIsRegionalPlanning={handleSetIsRegionalPlanning}
                    isContractualPlanning={isContractualPlanning()}
                    isShowOnlyCompletedPlans={false}
                    onApply={onApplyChanges}
                    onReloadPartnerInfo={onReloadPartnerInfo}
                    originalCurrency={originalCurrency}
                    originalActualsValueType={originalActualsValueType}
                    translationFactors={translationFactors}
                    partnerFlagsState={partnerFlagsState}
                    splitType={splitType}
                    setSplitType={handleSetSplitType}
                    globalCategory={globalCategory}
                    displaySettings={displaySettings}
                    showActuals={isShowActuals || IS_ALWAYS_SHOW_ACTUALS}
                    setShowActuals={setIsShowActuals}
                    showQuota={displaySettings.settings.isShowQuotaTargets}
                    setShowQuota={displaySettings.actions.setIsShowQuotaTargets}
                    isDistributorPlanner={isDistributor}
                    roundingValue={roundingValue}
                    setRoundingValue={handleSetRoundingValue}
                    roundingValueOriginal={roundingValueOriginal}
                    isShowValidationPopup={isShowValidationPopup}
                    setIsShowValidationPopup={setIsShowValidationPopup}
                    isShowTotal={isShowTotal}
                    additionalInfo={additionalInfo}
                    onSaveSuccess={handlePlanningSaveSuccess}
                    urlActualsValueType={urlActualsValueType}
                  />
                )
              } else {
                return (
                  <CategoryPlanningStandard
                    key={`jbp-planning-${settings.category}-${idx}`}
                    {...props}
                    planningData={planningData}
                    mapData={mapData}
                    dataSetInfo={settings}
                    allDataSets={hrDataSets}
                    formats={hrFormats.filter((el) => el.Category === settings.category)}
                    showActuals={isShowActuals || IS_ALWAYS_SHOW_ACTUALS}
                    setShowActuals={setIsShowActuals}
                    showQuota={displaySettings.settings.isShowQuotaTargets}
                    setShowQuota={displaySettings.actions.setIsShowQuotaTargets}
                    isShowFutureYearsPlans={true}
                    isRegionalPlanning={isRegionalPlanning}
                    setIsRegionalPlanning={handleSetIsRegionalPlanning}
                    isContractualPlanning={isContractualPlanning()}
                    isShowOnlyCompletedPlans={false}
                    setIsShowOnlyCompletedPlans={() => {}}
                    onApply={onApplyChanges}
                    onReloadPartnerInfo={onReloadPartnerInfo}
                    originalCurrency={originalCurrency}
                    originalActualsValueType={originalActualsValueType}
                    translationFactors={translationFactors}
                    partnerFlagsState={partnerFlagsState}
                    splitType={splitType}
                    setSplitType={handleSetSplitType}
                    globalCategory={globalCategory}
                    isShowComposedSoB={isShowComposedSoB}
                    setIsShowComposedSoB={handleSetIsShowComposedSoB}
                    isShowPrevTargets={displaySettings.settings.isShowPreviousPlans}
                    setIsShowPrevTargets={displaySettings.actions.setIsShowPreviousPlans}
                    onSaveSuccess={handlePlanningSaveSuccess}
                    urlActualsValueType={urlActualsValueType}
                  />
                )
              }
            })}
        </Fragment>
      )}
      {/** -------------------- Dialogs ----------------------- */}
      <ModalDialog
        show={isShowJbpPlanReturnModal}
        size="xs"
        header={() => <div className={styles.returnFlowModalHeader}>Changes saved</div>}
        content={JbpPlanReturnModalContent}
        contentProps={{
          onStay: handleStayInPlanning,
          onGoBack: handleReturnToPreviousTab,
        }}
        onClose={handleStayInPlanning}
      />
      <ModalDialog
        show={isShowErrorLoadingDataDialog}
        size="xs"
        onClose={() => setIsShowErrorLoadingDataDialog(false)}
        content={ErrorLoadingDataDialogContent}
        contentProps={{ onConfirm: () => setIsShowErrorLoadingDataDialog(false) }}
      />
      <ConfirmMpsTargetsSplitDialog
        isShowDialog={isShowMpsTargetsSplitDialog}
        mpsSplitMessage={partnerInfo?.MPSSplitMessage || ''}
        onConfirm={async () => {
          setIsShowMpsTargetsSplitDialog(false)
          await updateMPSTargetsSplit()
        }}
        onClose={() => {
          setIsShowMpsTargetsSplitDialog(false)
        }}
      />
      <MpsPlanNotCompletedDialog
        isShowDialog={
          isShowMpsPlanNotCompletedDialog && !!partnerInfo?.MPSPlanIsNotCompletedMessage
        }
        message={partnerInfo?.MPSPlanIsNotCompletedMessage || ''}
        onClose={() => {
          setIsShowMpsPlanNotCompletedDialog(false)
        }}
      />
    </div>
  )
}
